import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  PlayArrow,
  Delete,
  MusicNote,
  CloudUpload,
  Share,
  Close,
} from '@mui/icons-material';
import { SessionManager, RecentFile } from './SessionManager';
import { AuditioBackendManager } from './AuditioBackend';
import ConfirmationDialog from './components/ConfirmationDialog';
import DeletionInfoIcon from './components/DeletionInfoIcon';
import DriveShareDialog from './components/DriveShareDialog';
import { useAuth } from './contexts/AuthContext';
import { GoogleDriveService } from './services/GoogleDriveService';
import { getTrackType, renderTrackTypeIcon } from './utils/trackTypeUtils';

interface RecentFilesViewProps {
  onFileSelect: (fileId: number) => void;
  onClearHistory?: () => void;
  onUploadClick?: () => void;
  auditioBackendManager?: AuditioBackendManager;
  refreshTrigger?: number; // Add refresh trigger prop
  isSidebar?: boolean; // New prop to indicate if this is in a sidebar
  onDeleteAllFiles?: () => void; // Callback to expose delete function
}

const RecentFilesView: React.FC<RecentFilesViewProps> = ({
  onFileSelect,
  onClearHistory,
  onUploadClick,
  auditioBackendManager,
  refreshTrigger,
  isSidebar = false,
  onDeleteAllFiles
}) => {
  const { user } = useAuth();
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>([]);
  const [sessionStats, setSessionStats] = useState({
    totalFiles: 0,
    sessionDuration: 0,
    lastActivity: 0
  });
  const [showShareSnackbar, setShowShareSnackbar] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [showRemoveSharedDialog, setShowRemoveSharedDialog] = useState<{ open: boolean, fileId: number | null }>({ open: false, fileId: null });
  const [showDriveShareDialog, setShowDriveShareDialog] = useState(false);
  const [driveFileData, setDriveFileData] = useState<{fileId: string, fileUrl?: string} | null>(null);
  const [pendingFileId, setPendingFileId] = useState<number | null>(null);
  const [ownerInfoCache, setOwnerInfoCache] = useState<Map<number, number | null>>(new Map());
  const [fileMetadataCache, setFileMetadataCache] = useState<Map<number, {
    driveFileId?: string;
    driveFileUrl?: string;
    youtubeUrl?: string;
    ownerDisplayName?: string;
  }>>(new Map());
  const fetchingMetadataRef = useRef<Set<number>>(new Set());

  const confirmDeleteAllFiles = async () => {
    setShowDeleteAllDialog(false);
    if (onDeleteAllFiles) {
      onDeleteAllFiles();
    }
  };


  useEffect(() => {
    // Load data (session is already initialized in App.tsx)
    loadRecentFiles();
    loadSessionStats();

    // Set up periodic refresh
    const interval = setInterval(() => {
      loadRecentFiles();
      loadSessionStats();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [refreshTrigger, user]); // Add user to dependency array

  // Force refresh when component mounts (when navigating back to home page)
  useEffect(() => {
    loadRecentFiles();
    loadSessionStats();
  }, []); // Empty dependency array - only runs on mount

  const loadRecentFiles = () => {
    // Clean up session data first to remove any duplicates
    SessionManager.cleanupSession();
    
    const files = SessionManager.getRecentFiles();
    const stats = SessionManager.getSessionStats();
    setRecentFiles(files);
    setSessionStats(stats);
    
    // Fetch owner information for files that don't have it cached
    if (auditioBackendManager && user) {
      fetchOwnerInfoForFiles(files);
    }
  };

  // Fetch owner information and file metadata for files that don't have it cached
  const fetchOwnerInfoForFiles = useCallback(async (files: RecentFile[]) => {
    if (!auditioBackendManager) return;

    // Always fetch metadata for all files to ensure we have track type info
    // This ensures icons display correctly
    // Filter files that don't have metadata cached and aren't currently being fetched
    const filesToFetch = files.filter(file => {
      const isCached = fileMetadataCache.has(file.id);
      const isFetching = fetchingMetadataRef.current.has(file.id);
      if (!isCached && !isFetching) {
        // Mark as fetching
        fetchingMetadataRef.current.add(file.id);
        return true;
      }
      return false;
    });

    if (filesToFetch.length === 0) return;

    // Fetch owner info and metadata for all files in parallel
    const fetchPromises = filesToFetch.map(async (file) => {
      try {
        const fileData = await auditioBackendManager.getAudioFileById(file.id);
        if (fileData) {
          const ownerId = fileData.ownerId;
          // Update session data with ownerId
          if (file.ownerId === undefined) {
            SessionManager.addRecentFile(
              file.id,
              file.fileName,
              file.isUserUploaded,
              file.uploadTime,
              ownerId
            );
          }
          return { 
            fileId: file.id, 
            ownerId,
            metadata: {
              driveFileId: fileData.driveFileId,
              driveFileUrl: fileData.driveFileUrl,
              youtubeUrl: fileData.youtubeUrl,
              ownerDisplayName: fileData.ownerDisplayName
            }
          };
        } else {
          // File not found (404) - remove from session as it no longer exists
          console.log(`🗑️ File ${file.id} no longer exists, removing from recent files`);
          SessionManager.removeRecentFile(file.id);
        }
      } catch (error) {
        // Only log non-404 errors (404s are expected for deleted files)
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (!errorMessage.includes('404') && !errorMessage.includes('not found')) {
          console.error(`Error fetching owner info for file ${file.id}:`, error);
        }
      }
      return null;
    });

    const results = await Promise.all(fetchPromises);
    
    // Batch update caches
    const newOwnerCacheEntries: Array<[number, number | null]> = [];
    const newMetadataCacheEntries: Array<[number, {
      driveFileId?: string;
      driveFileUrl?: string;
      youtubeUrl?: string;
      ownerDisplayName?: string;
    }]> = [];
    
    results.forEach((r) => {
      if (r !== null) {
        if (r.ownerId !== undefined) {
          newOwnerCacheEntries.push([r.fileId, r.ownerId ?? null]);
        }
        if (r.metadata) {
          newMetadataCacheEntries.push([r.fileId, r.metadata]);
        }
      }
    });
    
    if (newOwnerCacheEntries.length > 0) {
      setOwnerInfoCache(prev => {
        const newCache = new Map(prev);
        newOwnerCacheEntries.forEach(([fileId, ownerId]) => {
          newCache.set(fileId, ownerId);
        });
        return newCache;
      });
    }
    
    if (newMetadataCacheEntries.length > 0) {
      setFileMetadataCache(prev => {
        const newCache = new Map(prev);
        newMetadataCacheEntries.forEach(([fileId, metadata]) => {
          newCache.set(fileId, metadata);
          // Remove from fetching set once cached
          fetchingMetadataRef.current.delete(fileId);
        });
        return newCache;
      });
    }
    
    // Remove all fetched files from fetching set (whether successful or not)
    filesToFetch.forEach(file => {
      fetchingMetadataRef.current.delete(file.id);
    });
    
    // Reload files to get updated ownerId
    const updatedFiles = SessionManager.getRecentFiles();
    setRecentFiles(updatedFiles);
  }, [auditioBackendManager, fileMetadataCache]);

  // Compute if file is uploaded by current user
  const isFileUploadedByUser = (file: RecentFile): boolean => {
    if (!user) return false;
    
    // Use ownerId from file if available, otherwise check cache
    const ownerId = file.ownerId !== undefined ? file.ownerId : ownerInfoCache.get(file.id);
    
    // If no ownerId, treat as shared (not uploaded by user)
    if (ownerId === null || ownerId === undefined) {
      return false;
    }
    
    // Compare ownerId with current user's id
    return Number(ownerId) === Number(user.id);
  };

  const loadSessionStats = () => {
    const stats = SessionManager.getSessionStats();
    setSessionStats(stats);
  };

  const handleFileSelect = (fileId: number) => {
    SessionManager.updateFileAccess(fileId);
    onFileSelect(fileId);
  };

  const handleClearHistory = () => {
    // Clear session data
    SessionManager.clearSession();
    SessionManager.initializeSession(); // Re-initialize after clearing
    loadRecentFiles();
    loadSessionStats();
    
    // Call original clear history callback if provided
    if (onClearHistory) {
      onClearHistory();
    }
  };

  const handleShare = async (fileId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    
    // Check if this is a Google Drive file
    if (auditioBackendManager) {
      try {
        const fileData = await auditioBackendManager.getAudioFileById(fileId);
        
        if (fileData?.driveFileId) {
          console.log('🔍 Google Drive file detected, checking permissions...');
          
          // Get Google Drive access token
          const driveService = new GoogleDriveService();
          let accessToken: string;
          try {
            accessToken = await driveService.getAccessToken();
          } catch (err) {
            console.error('❌ Failed to get Drive access token:', err);
            // If we can't get token, just copy the link
            const fileUrl = `${window.location.origin}/${fileId}`;
            await navigator.clipboard.writeText(fileUrl);
            setShowShareSnackbar(true);
            return;
          }

          // Check if file is publicly accessible
          try {
            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/drive/check-share`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                driveFileId: fileData.driveFileId,
                accessToken: accessToken
              })
            });

            if (response.ok) {
              const shareData = await response.json();
              
              // If not publicly accessible OR if we couldn't check permissions, show dialog
              if (!shareData.isPubliclyAccessible || !shareData.canShare) {
                // File is not public or we can't verify, show dialog
                setPendingFileId(fileId);
                setDriveFileData({
                  fileId: fileData.driveFileId,
                  fileUrl: shareData.fileUrl || fileData.driveFileUrl
                });
                setShowDriveShareDialog(true);
                return;
              }
            } else {
              // If check fails (including 500 errors), assume file needs to be shared
              console.warn('⚠️ Permission check failed (status:', response.status, '), assuming file needs to be shared');
              setPendingFileId(fileId);
              setDriveFileData({
                fileId: fileData.driveFileId,
                fileUrl: fileData.driveFileUrl
              });
              setShowDriveShareDialog(true);
              return;
            }
          } catch (err) {
            console.error('❌ Error checking file permissions:', err);
            // If check fails, assume file needs to be shared and show dialog
            setPendingFileId(fileId);
            setDriveFileData({
              fileId: fileData.driveFileId,
              fileUrl: fileData.driveFileUrl
            });
            setShowDriveShareDialog(true);
            return;
          }
        }
      } catch (err) {
        console.error('❌ Error checking file data:', err);
        // Continue with normal share if check fails
      }
    }
    
    // Normal share flow (non-Drive file or already public Drive file)
    const fileUrl = `${window.location.origin}/${fileId}`;
    
    try {
      await navigator.clipboard.writeText(fileUrl);
      setShowShareSnackbar(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fileUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowShareSnackbar(true);
    }
  };

  const handleRemoveSharedFile = (fileId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    setShowRemoveSharedDialog({ open: true, fileId });
  };

  const confirmRemoveSharedFile = () => {
    if (showRemoveSharedDialog.fileId !== null) {
      SessionManager.removeRecentFile(showRemoveSharedDialog.fileId);
      loadRecentFiles();
      loadSessionStats();
    }
    setShowRemoveSharedDialog({ open: false, fileId: null });
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };


  if (recentFiles.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={isSidebar ? 2 : 4}
        textAlign="center"
      >
        <MusicNote sx={{ fontSize: isSidebar ? 32 : 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant={isSidebar ? "body1" : "h6"} color="text.secondary" gutterBottom sx={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: isSidebar ? '1rem' : '1.2rem',
          fontWeight: 500
        }}>
          {isSidebar ? "No recent tracks" : "Welcome to Auditio!"}
        </Typography>
        {!isSidebar && (
          <>
            <Typography variant="body1" sx={{
              color: 'var(--text-color)',
              mb: 3,
              opacity: 0.8,
              fontSize: '1rem',
              lineHeight: 1.6
            }}>
              Upload an audio file to get started. Your recent tracks will appear here.
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={onUploadClick}
            >
              Upload Your First File
            </Button>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: isSidebar ? '100%' : 800, 
      mx: isSidebar ? 0 : 'auto', 
      p: isSidebar ? 1 : 2 
    }}>
      {/* Title - Only show on main page, not in sidebar */}
      {!isSidebar && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          {/* Font test */}
          <div className="font-test">Font Test: Soria</div>
          <Typography variant="h4" component="h1" sx={{ 
            fontFamily: 'Soria, serif !important', 
            fontWeight: 700,
            fontSize: '2.5rem',
            color: 'primary.main'
          }}>
            Recent Tracks
          </Typography>
        </Box>
      )}

      {/* Upload Button - Only show on main page, not in sidebar */}
      {!isSidebar && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={onUploadClick}
            >
              Upload New File
            </Button>
            
            {recentFiles.length > 0 && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Delete />}
                onClick={() => setShowDeleteAllDialog(true)}
              >
                Delete All Uploaded Files
              </Button>
            )}
            
          </Box>
        </Box>
      )}

      {/* Test button for sidebar too */}
      {/* Removed test button for sidebar */}

      {/* Recent Files List */}
      {recentFiles.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{
              fontSize: '1.1rem',
              fontWeight: 500
            }}>
              No recent tracks
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{
              fontSize: '0.9rem'
            }}>
              Upload a track to get started
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSidebar ? 1 : 2 }}>
          {recentFiles.map((file) => (
            isSidebar ? (
              // Sidebar file item style
              <Box 
                key={file.id} 
                className="file-item"
                onClick={() => handleFileSelect(file.id)}
              >
                <Box className="file-header">
                  <Box className="file-icon-name" sx={{ flexDirection: 'row', alignItems: 'center', width: 100, gap: 0.75 }}>
                    {(() => {
                      const metadata = fileMetadataCache.get(file.id);
                      // If metadata not in cache, use empty object (will default to uploaded icon)
                      // The fetchOwnerInfoForFiles will populate it on next render
                      const trackType = getTrackType(metadata || {});
                      return renderTrackTypeIcon(trackType.type, undefined, 36);
                    })()}
                    <Typography className="file-name">
                      {file.fileName}
                    </Typography>
                  </Box>
                </Box>
                <Box className="file-meta">
                  {(() => {
                    const metadata = fileMetadataCache.get(file.id);
                    const ownerName = metadata?.ownerDisplayName || 'Unknown';
                    return (
                      <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }}>
                        By {ownerName}
                      </Typography>
                    );
                  })()}
                  <Box className="file-actions">
                    {/* Deletion info icon - only show for user-uploaded files */}
                    {isFileUploadedByUser(file) && <DeletionInfoIcon file={file} size="small" />}
                    <IconButton
                      className="file-action-btn"
                      onClick={(e) => handleShare(file.id, e)}
                      size="small"
                    >
                      <Share />
                    </IconButton>
                    {!isFileUploadedByUser(file) && (
                      <IconButton
                        className="file-action-btn remove"
                        onClick={(e) => handleRemoveSharedFile(file.id, e)}
                        size="small"
                      >
                        <Close />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Box className="file-times">
                  <Box className="file-time">
                    <svg viewBox="0 0 24 24">
                      <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                    </svg>
                    Created: {new Date(file.uploadTime).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </Box>
                  <Box className="file-time">
                    <svg viewBox="0 0 24 24">
                      <path d="M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9M12,4.5C17,4.5 21.27,7.61 23,12C21.27,16.39 17,19.5 12,19.5C7,19.5 2.73,16.39 1,12C2.73,7.61 7,4.5 12,4.5M3.18,12C4.83,15.36 8.24,17.5 12,17.5C15.76,17.5 19.17,15.36 20.82,12C19.17,8.64 15.76,6.5 12,6.5C8.24,6.5 4.83,8.64 3.18,12Z"/>
                    </svg>
                    Accessed: {formatTime(file.lastAccessed)}
                  </Box>
                </Box>
              </Box>
            ) : (
              // Main page card style
              <Card 
                key={file.id} 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { 
                    bgcolor: 'action.hover',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
                onClick={() => handleFileSelect(file.id)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                        {(() => {
                          const metadata = fileMetadataCache.get(file.id) || {};
                          const trackType = getTrackType(metadata);
                          return renderTrackTypeIcon(trackType.type, undefined, 36);
                        })()}
                        <Typography variant="h6" component="h3">
                          {file.fileName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        {(() => {
                          const metadata = fileMetadataCache.get(file.id);
                          const ownerName = metadata?.ownerDisplayName || 'Unknown';
                          return (
                            <Typography variant="body2" color="text.secondary" sx={{
                              fontSize: '0.85rem'
                            }}>
                              By {ownerName}
                            </Typography>
                          );
                        })()}
                        <Typography variant="body2" color="text.secondary" sx={{
                          fontSize: '0.85rem'
                        }}>
                          • Last accessed {formatTime(file.lastAccessed)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{
                        fontSize: '0.85rem'
                      }}>
                        Uploaded {formatTime(file.uploadTime)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        onClick={(e) => handleShare(file.id, e)}
                        size="small"
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { 
                            bgcolor: 'primary.light',
                            color: 'white'
                          }
                        }}
                      >
                        <Share />
                      </IconButton>
                      {!isFileUploadedByUser(file) && (
                        <IconButton
                          onClick={(e) => handleRemoveSharedFile(file.id, e)}
                          size="small"
                          sx={{ 
                            color: 'error.main',
                            '&:hover': { 
                              bgcolor: 'error.light',
                              color: 'white'
                            }
                          }}
                        >
                          <Close />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )
          ))}
        </Box>
      )}
      
      {/* Share Success Snackbar */}
      <Snackbar
        open={showShareSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowShareSnackbar(false)}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Google Drive Share Dialog */}
      <DriveShareDialog
        open={showDriveShareDialog}
        onClose={() => {
          setShowDriveShareDialog(false);
          setDriveFileData(null);
          setPendingFileId(null);
        }}
        onOpenInDrive={() => {
          // Copy the Auditio link first
          if (pendingFileId !== null) {
            const fileUrl = `${window.location.origin}/${pendingFileId}`;
            navigator.clipboard.writeText(fileUrl).then(() => {
              setShowShareSnackbar(true);
            }).catch(() => {
              // Fallback
              const textArea = document.createElement('textarea');
              textArea.value = fileUrl;
              document.body.appendChild(textArea);
              textArea.select();
              document.execCommand('copy');
              document.body.removeChild(textArea);
              setShowShareSnackbar(true);
            });
          }
          
          // Open Google Drive
          if (driveFileData?.fileUrl) {
            window.open(driveFileData.fileUrl, '_blank');
          }
          setShowDriveShareDialog(false);
          setDriveFileData(null);
          setPendingFileId(null);
        }}
        fileUrl={driveFileData?.fileUrl}
      />
      

      <ConfirmationDialog
        open={showDeleteAllDialog}
        title="Delete All Uploaded Files?"
        message="Are you sure you want to delete all uploaded files? This action cannot be undone."
        confirmText="Delete All"
        severity="error"
        onConfirm={confirmDeleteAllFiles}
        onCancel={() => setShowDeleteAllDialog(false)}
      />
      <ConfirmationDialog
        open={showRemoveSharedDialog.open}
        title="Remove Shared File?"
        message="Are you sure you want to remove this shared file from your recent files?"
        confirmText="Remove"
        severity="warning"
        onConfirm={confirmRemoveSharedFile}
        onCancel={() => setShowRemoveSharedDialog({ open: false, fileId: null })}
      />
    </Box>
  );
};

export default RecentFilesView; 